import type {GetAgentsResponse, GetLanguageSettingsResponse, GetUserResponse} from "@/practice/api";

// The home screen's small responses from the last visit. On reload they name
// the user before /get_user answers, so the curriculum, sessions and
// kickstarts can be read from the conditional response cache and requested
// at page load, and the home screen can render from them right away.
// wipeClientStorage() clears it on logout; clearAuthenticatedSession() and
// every login clear it so one account's home never shows for another.

const HOME_SNAPSHOT_KEY = 'issen_home_snapshot_v1';

export type HomeSnapshot = {
    userId: string;
    user: GetUserResponse | null;
    languageSettings: GetLanguageSettingsResponse | null;
    agents: GetAgentsResponse | null;
};

function storageOrNull(): Storage | null {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
        return null;
    }
}

export function readHomeSnapshot(): HomeSnapshot | null {
    try {
        const raw = storageOrNull()?.getItem(HOME_SNAPSHOT_KEY);
        if (raw === null || raw === undefined) {
            return null;
        }
        const snapshot = JSON.parse(raw) as HomeSnapshot;
        return typeof snapshot.userId === 'string' && snapshot.userId.length > 0 ? snapshot : null;
    } catch {
        return null;
    }
}

// Only what the home renders before /get_user answers is kept: account,
// billing, attribution and goal/interest fields stay out of storage.
function storedFields(fields: Partial<Omit<HomeSnapshot, 'userId'>>): Partial<Omit<HomeSnapshot, 'userId'>> {
    const stored = {...fields};
    if (fields.user) {
        stored.user = {user: {
            ...fields.user.user,
            username: '',
            stripeCustomerId: undefined,
            ageGroup: undefined,
            marketingSource: undefined,
            attributedCampaignSlug: undefined,
            attributedRedirectClickId: undefined,
            attributedPlatform: undefined,
            attributedNetwork: undefined,
        }};
    }
    if (fields.languageSettings) {
        stored.languageSettings = {
            ...fields.languageSettings,
            languageSettings: {
                ...fields.languageSettings.languageSettings,
                languageGoal1: null,
                languageGoal2: null,
                interest1: null,
                interest2: null,
                introPrompt: null,
                conversationPrompt: null,
            },
        };
    }
    return stored;
}

export function updateHomeSnapshot(userId: string, fields: Partial<Omit<HomeSnapshot, 'userId'>>): void {
    const current = readHomeSnapshot();
    const base: HomeSnapshot = current !== null && current.userId === userId
        ? current
        : {userId, user: null, languageSettings: null, agents: null};
    try {
        storageOrNull()?.setItem(HOME_SNAPSHOT_KEY, JSON.stringify({...base, ...storedFields(fields)}));
    } catch {
        // Without a snapshot the next load waits for the network.
    }
}

export function clearHomeSnapshot(): void {
    try {
        storageOrNull()?.removeItem(HOME_SNAPSHOT_KEY);
    } catch {
        // A stale snapshot is replaced after the next /get_user.
    }
}
